import { StyleSheet, View, Text } from "react-native";
import React from "react";

import { AttachmentChip, AttachmentChipProps } from "../attachmentChip/AttachmentChip";
import { colors, radii, spacing, typographyStyles } from "../../../../styles/design-system";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";
import { MessageActionBar } from "./MessageActionBar";
import type { ReadAloudControl } from "./ReadAloudPlaybackControl";
import type { ChatBlock } from "../../../../containers/chat/aiTutorMessage.types";
import { ImageCard } from "../inlineCards/ImageCard";
import { VideoCard } from "../inlineCards/VideoCard";
import { InlineResponseCard } from "../inlineCards/InlineResponseCard";
import { SubjectiveCard } from "../inlineCards/SubjectiveCard";
import { VisualizationCard } from "../visualizationCard/VisualizationCard";
import type { MessageVisualizationBlock } from "../visualizationModal/VisualizationModal";
import StreamingCursor from "./StreamingCursor";

export type { ReadAloudControl, ReadAloudStatus } from "./ReadAloudPlaybackControl";

const userMarkdownStyles = {
  paragraph: { marginTop: 0, marginBottom: 0 },
};

const aiMarkdownStyles = {
  paragraph: { lineHeight: typographyStyles.textLg.lineHeight },
  bullet_list: { lineHeight: typographyStyles.textLg.lineHeight },
  ordered_list: { lineHeight: typographyStyles.textLg.lineHeight },
};

export interface AITutorMessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  taskAttachmentChips?: AttachmentChipProps[];
  isArchived: boolean;
  isFullWidth?: boolean;
  onFeedback?: (value: "up" | "down" | null) => void;
  feedback?: "up" | "down";
  readAloudControl?: ReadAloudControl;
  hideReadAloudControl?: boolean;
  embedYouTube?: boolean;
  isStreaming?: boolean;
  blocks?: ChatBlock[];
  onChoiceSelect?: (blockIndex: number, choiceIndex: number) => void;
  onOpenVisualization?: (block: MessageVisualizationBlock) => void;
}

export const AITutorMessageBubble = ({
  content,
  role,
  timestamp,
  taskAttachmentChips = [],
  isArchived,
  isFullWidth = false,
  onFeedback,
  feedback,
  readAloudControl,
  hideReadAloudControl = false,
  embedYouTube = true,
  isStreaming = false,
  blocks = [],
  onChoiceSelect,
  onOpenVisualization,
}: AITutorMessageBubbleProps) => {
  const messageStyles = [styles.messageBubble, role === "user" ? styles.userMessage : styles.assistantMessage];
  const bubbleStyles = [
    styles.bubble,
    role === "user" ? styles.userBubble : styles.assistantBubble,
    role === "user" && isArchived && styles.userArchivedBubble,
  ];
  const messageWrapperStyles = [
    styles.messageWrapper,
    role === "user" ? styles.userMessageWrapper : styles.assistantMessageWrapper,
    isFullWidth && styles.messageWrapperFullWidth,
  ];
  const textStyles = [
    styles.message,
    typographyStyles.textBase,
    role === "user" ? styles.userText : styles.assistantText,
    role === "user" && isArchived && styles.userArchivedText,
  ];

  const formattedTimestamp =
    role === "user"
      ? new Date(timestamp).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : undefined;

  return (
    <View style={messageStyles}>
      <View style={messageWrapperStyles}>
        {(taskAttachmentChips ?? []).map((attachment, index) => (
          <AttachmentChip key={index} {...attachment} removeChip={isArchived ? undefined : attachment?.removeChip} />
        ))}
        <View style={bubbleStyles}>
          <CustomMarkdown
            style={{
              body: textStyles,
              math_inline: [role === "user" && styles.userText, { fontSize: typographyStyles.textBase.fontSize }],
              math_block: [role === "user" && styles.userText, { fontSize: typographyStyles.textBase.fontSize }],
              ...(role === "user" ? userMarkdownStyles : aiMarkdownStyles),
            }}
            content={content}
            embedYouTube={embedYouTube}
            enableLearningCallouts={role === "assistant"}
          />
          {isStreaming && role === "assistant" && <StreamingCursor />}
          {blocks.map((block, blockIndex) => {
            if (block.kind === "image") {
              return (
                <ImageCard
                  key={`image-${blockIndex}`}
                  status={block.status}
                  url={block.url ?? undefined}
                  title={block.title ?? undefined}
                  description={block.description ?? undefined}
                />
              );
            }

            if (block.kind === "video") {
              return (
                <VideoCard
                  key={`video-${blockIndex}`}
                  videoId={block.videoId ?? undefined}
                  title={block.title ?? undefined}
                  url={block.url}
                  thumbnailUrl={block.thumbnailUrl ?? undefined}
                />
              );
            }

            if (block.kind === "mcq") {
              return (
                <InlineResponseCard
                  key={`mcq-${block.questionId ?? blockIndex}`}
                  question={block.question}
                  choices={block.choices}
                  selectedChoiceIndex={block.selectedChoiceIndex}
                  submitted={block.submitted}
                  onSelectChoice={
                    !isArchived && onChoiceSelect ? (choiceIndex) => onChoiceSelect(blockIndex, choiceIndex) : undefined
                  }
                  onSubmit={undefined}
                  colorVariant="mcq"
                />
              );
            }

            if (block.kind === "inline_response") {
              return (
                <InlineResponseCard
                  key={`inline-response-${blockIndex}`}
                  question={block.question}
                  choices={block.choices}
                  selectedChoiceIndex={block.selectedChoiceIndex}
                  submitted={block.submitted}
                  onSelectChoice={
                    !isArchived && onChoiceSelect ? (choiceIndex) => onChoiceSelect(blockIndex, choiceIndex) : undefined
                  }
                  onSubmit={undefined}
                  colorVariant="default"
                />
              );
            }

            if (block.kind === "subjective") {
              return <SubjectiveCard key={`subjective-${block.questionId ?? blockIndex}`} question={block.question} />;
            }

            if (block.kind === "visualization" && onOpenVisualization) {
              return (
                <VisualizationCard
                  key={`viz-${blockIndex}`}
                  visualizationId={block.visualizationId}
                  visualizationType={block.visualizationType}
                  visualizationDescription={block.visualizationDescription}
                  onPress={() =>
                    onOpenVisualization({
                      visualizationId: block.visualizationId,
                      visualizationType: block.visualizationType,
                      visualizationDescription: block.visualizationDescription,
                      content: block.content,
                    })
                  }
                />
              );
            }

            return null;
          })}
        </View>
        {role === "assistant" && (
          <MessageActionBar
            content={content}
            feedback={feedback}
            onFeedback={!isArchived ? onFeedback : undefined}
            readAloudControl={readAloudControl}
            hideReadAloudControl={hideReadAloudControl}
          />
        )}
        {formattedTimestamp ? <Text style={styles.timestamp}>{formattedTimestamp}</Text> : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  messageBubble: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "flex-start",
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  userMessage: {
    flexDirection: "row-reverse",
  },
  assistantMessage: {
    flexDirection: "row",
  },
  bubble: {
    borderRadius: radii.lg,
  },
  userBubble: {
    padding: spacing.sm,
    borderTopRightRadius: radii.sm,
    backgroundColor: colors.brand[500],
  },
  userArchivedBubble: {
    backgroundColor: colors.grey[400],
  },
  userArchivedText: {
    color: colors.white,
  },
  assistantBubble: {
    backgroundColor: "transparent",
  },
  messageWrapper: {
    display: "flex",
    maxWidth: "90%",
  },
  messageWrapperFullWidth: {
    maxWidth: "100%",
    width: "100%",
  },
  userMessageWrapper: {
    alignItems: "flex-end",
  },
  assistantMessageWrapper: {
    alignItems: "flex-start",
  },
  message: {
    textWrap: "wrap",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    // @ts-expect-error - works for web
    display: "block",
  },
  userText: {
    textAlign: "right",
    color: colors.white,
  },
  assistantText: {
    textAlign: "left",
    color: colors.grey["700"],
    // @ts-expect-error - works for web
    display: "block",
  },
  timestamp: {
    color: colors.grey[500],
    ...typographyStyles.textXs,
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
  },
});
